import CategoryOrSubcategoryPage, {
  CategoryOrSubcategoryPageProps,
} from '@c/CategoryOrSubcategoryPage';
import { DefaultLayout } from '@c/layouts';
import SEO from '@c/SEO';
import { BreadcrumbSchema, FAQSchema } from '@c/StructuredData';
import { getProducts } from '@util/firestore/products/';
import { getCategoryBySlug } from '@util/firestore/categories';
import { getProductQueryArgsFromContext } from '@util/getPropsFromConext';
import { toSerializable } from '@util/serializeFirestore';
import { GetServerSideProps } from 'next';

export default function CategoryPage({
  slugToTitle,
  keyword,
  results,
  pageInfo,
  initialTopFilters,
  initialSideFilters,
  categoryOptions,
  conditionOptions,
  brandOptions
}: CategoryOrSubcategoryPageProps) {
  // Generate breadcrumb items for structured data
  const breadcrumbItems = [
    { name: 'Home', url: 'https://gearfocus.com/' },
    { name: 'Products', url: 'https://gearfocus.com/products' }
  ];

  const category = initialSideFilters.category
  
  // Add category path to breadcrumbs
  if (category) {
    breadcrumbItems.push({
      name: category.name,
      url: `https://gearfocus.com/c/${category.slug}`
    });
  }
  
  // SEO metadata
  const pageTitle = category
    ? (category.metadata?.title || `${category.name} - Photography Equipment`)
    : (slugToTitle ? `${slugToTitle} - Photography Equipment` : 'Camera Equipment');
  
  const pageDescription = category?.metadata?.description || 
    `Explore our collection of ${category?.name || 'camera'} equipment and accessories. Find the best deals on new and used photography gear.`;
  
  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        ogImage={category?.image || '/images/camera-collection.jpg'}
        keywords={category?.metadata?.keywords?.split(',') || [
          category?.name || '', 'photography equipment', 'camera gear',
          'used cameras', 'camera lenses', 'photography accessories'
        ]}
      />

      {/* Breadcrumb Schema */}
      <BreadcrumbSchema items={breadcrumbItems} />

      {/* FAQ Schema — only emitted when the category has well-formed FAQ content.
          category.faq comes straight from Firestore with no zod validation on
          this read path (getCategoryBySlug), so a malformed value (a string,
          for which .length is also truthy, or an entry missing question/answer)
          must be filtered here rather than trusted — otherwise FAQSchema's
          faqs.map throws during this page component's SSR render and
          500s the whole page. (FAQContentSection, the visible FAQ UI
          rendered by CategoryOrSubcategoryPage, has the identical guard
          on the identical data — this file's guard only covers the
          JSON-LD emission, a separate render path.) */}
      {(() => {
        const faq = category?.faq;
        if (!Array.isArray(faq)) return null;
        const validFaqs = faq.filter(
          (f): f is { question: string; answer: string } =>
            !!f && typeof f.question === 'string' && f.question.length > 0 &&
            typeof f.answer === 'string' && f.answer.length > 0
        );
        return validFaqs.length > 0 ? <FAQSchema faqs={validFaqs} /> : null;
      })()}

      <CategoryOrSubcategoryPage
        categoryOptions={categoryOptions}
        slugToTitle={slugToTitle}
        keyword={keyword}
        results={results}
        pageInfo={pageInfo}
        initialTopFilters={initialTopFilters}
        initialSideFilters={initialSideFilters}
        seller_id={null}
        brandOptions={brandOptions}
        conditionOptions={conditionOptions}
      />
    </>
  );
}

CategoryPage.getLayout = function getLayout(page: React.ReactElement) {
  return <DefaultLayout>{page}</DefaultLayout>;
};

export const getServerSideProps: GetServerSideProps<
  CategoryOrSubcategoryPageProps
> = async (context) => {
  context.res.setHeader(
    'Cache-Control',
    'public, max-age=0, s-maxage=3600, stale-while-revalidate=86400'
  );

  const args = await getProductQueryArgsFromContext(context);

  const category_slugs = (context.query.slugs as string[] | undefined) ?? null;
  if (category_slugs && category_slugs.length > 0) args.last_slug = category_slugs.slice(-1)[0];
  
  const currentCategory = await getCategoryBySlug(args.last_slug ?? null) || null
   
  if (!currentCategory) {
    return {
      redirect: {
        destination: `/products`,
        permanent: false
      }
    }
  }

  const response = await getProducts(args, currentCategory); 

  const slugToTitle = args.last_slug?.split('-').filter(word => word).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  
  // Trim brandOptions to filter-only fields (2 of 7) — saves ~200 KB per page
  const BRAND_KEYS = new Set(['id', 'name']);
  const trimBrand = (b: Record<string, unknown>) => {
    const t: Record<string, unknown> = {};
    for (const k of BRAND_KEYS) if (k in b) t[k] = b[k];
    return t;
  };

  // Trim categoryOptions to filter-only fields (5 of 14) — saves ~110 KB per page
  const CAT_KEYS = new Set(['id', 'name', 'level', 'parents']);
  const trimCat = (c: Record<string, unknown>) => {
    const t: Record<string, unknown> = {};
    for (const k of CAT_KEYS) if (k in c) t[k] = c[k];
    return t;
  };

  // Trim listings to display-only fields — saves ~40-50 KB per page
  const LISTING_KEYS = new Set([
    'id', 'title', 'slug', 'thumbnail', 'images', 'price', 'og_price',
    'on_sale', 'out_of_stock', 'stock', 'condition', 'brand', 'categories',
    'sku', 'bumps', 'favorite_count', 'is_auction', 'start_time', 'end_time',
    'is_verified', 'is_flat_rate', 'seller_id', 'shipping_costs', 'gearMaster',
    'external_source', 'integrations', 'description', 'account_id',
    'date_added', 'rank_score', // needed by useProductsPage for client-side sorting
  ]);
  const trimListing = (l: Record<string, unknown>) => {
    const t: Record<string, unknown> = {};
    for (const k of LISTING_KEYS) if (k in l) t[k] = l[k];
    return t;
  };

  return {
    // `slugToTitle` is undefined for paths that don't resolve to a title, which
    // Next rejects; toSerializable omits undefined keys.
    props: toSerializable({
      categoryOptions: args.categoryOptions.map(c => trimCat(c as Record<string, unknown>)) as typeof args.categoryOptions,
      conditionOptions: args.conditionOptions,
      category: currentCategory,
      slugToTitle: slugToTitle ?? undefined,
      brandOptions: args.brandOptions.map(b => trimBrand(b as Record<string, unknown>)) as typeof args.brandOptions,
      keyword: args.keyword,
      is_expired: false,
      is_verified: false,
      results: response.results.map(r => trimListing(r as Record<string, unknown>)) as typeof response.results,
      pageInfo: response.pageInfo,
      seller_id: null,
      initialTopFilters: {
        limit: args.limit,
        sort: args.sort,
        view: (context.query.view as 'list') || 'grid',
      },
      initialSideFilters: {
        minPrice: args.minPrice,
        maxPrice: args.maxPrice,
        brand: args.brandFilters,
        condition: args.conditionFilters,
        category: currentCategory,
        deals: args.deals,
        useCases: args.useCases,
        additional: args.additionalFilters
      }
    }),
  };
};
